import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { appCache } from '../lib/cache';

const { FiStar, FiZap, FiSearch, FiFilter, FiArrowRight, FiShoppingCart } = FiIcons;

const StarDisplay = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <SafeIcon 
        key={s} 
        icon={FiStar} 
        className={`text-sm ${s <= Math.round(value) ? 'text-yellow-400' : 'text-white/15'}`} 
        style={{ fill: s <= Math.round(value) ? '#facc15' : 'none' }} 
      />
    ))}
  </div>
);

const ReviewCard = ({ review, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.07, duration: 0.5 }}
    className="relative group"
  >
    <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-purple-600/20 via-transparent to-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
    <Link 
      to={`/reviews/${review.slug || review.id}`} 
      className="block relative bg-[#0a0118] border border-[#2a1f42] rounded-2xl overflow-hidden hover:border-purple-500/40 transition-all duration-300"
    >
      <div className="w-full h-44 overflow-hidden bg-[#0c0118]">
        {review.image_url ? (
          <img 
            src={review.image_url} 
            alt={review.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-600 flex items-center justify-center p-6 group-hover:scale-105 transition-transform duration-500">
            <h3 className="text-xl font-black text-white drop-shadow-lg text-center leading-tight tracking-tight line-clamp-3">
              {review.title}
            </h3>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {review.category && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[10px] font-bold uppercase tracking-wide">
              <SafeIcon icon={FiZap} className="text-[9px]" />
              {review.category}
            </span>
          )}
          {(review.tags || []).slice(0, 2).map((t, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/40 text-[10px]">{t}</span>
          ))}
        </div>
        <h3 className="text-white font-bold text-base mb-2 leading-snug line-clamp-2">{review.title}</h3>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <StarDisplay value={review.rating} />
            <span className="text-white/40 text-xs">{review.rating}/5</span>
          </div>
          <span className="text-purple-400 text-xs flex items-center gap-1 font-semibold">
            Read Review <SafeIcon icon={FiArrowRight} className="text-[10px]" />
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      if (appCache.reviews) {
        setReviews(appCache.reviews);
        setLoading(false);
        return;
      }
      try {
        const { data } = await supabase
          .from('tool_reviews_1776536215359')
          .select('id, title, slug, tool_name, rating, image_url, tags, category, created_at')
          .eq('status', 'published')
          .order('created_at', { ascending: false });
        
        if (data) {
          appCache.reviews = data;
          setReviews(data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const categories = [...new Set(reviews.map(r => r.category).filter(Boolean))];
  
  const filtered = reviews.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || 
      r.title.toLowerCase().includes(q) || 
      r.tool_name.toLowerCase().includes(q) || 
      (r.tags || []).some(t => t.toLowerCase().includes(q));
    const matchCat = filterCat === 'all' || r.category === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <div className="w-full">
      {/* Hero */}
      <motion.section 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative py-24 px-4 text-center overflow-hidden"
      >
        <div className="absolute top-10 left-1/4 w-72 h-72 bg-purple-700/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-56 h-56 bg-indigo-700/10 rounded-full blur-3xl pointer-events-none" />

        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-widest mb-6"
        >
          <SafeIcon icon={FiStar} className="text-yellow-400" style={{ fill: '#facc15' }} />
          Tool Reviews
        </motion.div>

        <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 bg-clip-text text-transparent">Fiverring</span>
          <br /><span className="text-white/90 text-4xl md:text-5xl font-bold">Reviews</span>
        </h1>
        <p className="text-white/50 text-lg max-w-xl mx-auto mb-4">
          In-depth reviews of the best AI tools, written by our team.
        </p>

        <div className="max-w-lg mx-auto mb-3 px-4 py-3 bg-yellow-500/5 border border-yellow-500/20 rounded-xl text-yellow-200/70 text-xs leading-relaxed">
          <strong className="text-yellow-300">Disclosure:</strong> This page contains affiliate links. If you make a purchase through these links, I may earn a commission at no extra cost to you.
        </div>

        <div className="flex flex-wrap justify-center gap-6 mt-8">
          {[
            { label: 'Reviews', value: reviews.length },
            { label: 'Avg Rating', value: reviews.length ? (reviews.reduce((a,b) => a + b.rating, 0) / reviews.length).toFixed(1) : '—' },
            { label: 'Categories', value: categories.length }
          ].map((s, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + (i * 0.1) }}
              className="bg-[#0a0118] border border-[#2a1f42] rounded-2xl px-8 py-4 text-center"
            >
              <p className="text-3xl font-black bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">{s.value}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-0.5">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap gap-3 items-center mb-8 bg-[#0a0118] border border-[#2a1f42] rounded-2xl p-4"
        >
          <SafeIcon icon={FiFilter} className="text-white/30" />
          <div className="relative flex-1 min-w-[180px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input 
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search reviews..."
              className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2 pl-9 pr-3 text-white text-sm focus:outline-none focus:border-purple-500"
            />
          </div>
          <select 
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            className="bg-[#150a28] border border-[#2a1f42] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-white/30 text-sm ml-auto">{filtered.length} review{filtered.length !== 1 ? 's' : ''}</span>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-24"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((r, i) => <ReviewCard key={r.id} review={r} index={i} />)}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24">
            <div className="w-20 h-20 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiStar} className="text-3xl text-purple-400/40" />
            </div>
            <h3 className="text-white/40 font-bold text-lg mb-1">No reviews yet</h3>
            <p className="text-white/25 text-sm">Check back soon for expert tool reviews.</p>
          </motion.div>
        )}
      </section>
    </div>
  );
};

export default Reviews;